// src/page/PackageEdit.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { TICKET_GENRE_OPTIONS } from '../constants/ticketOptions';
import { AREA_OPTIONS } from '../constants/spotOptions';

export default function PackageEdit() {
  useAuthGuard();
  const navigate = useNavigate();
  const { packageID } = useParams();

  // ---------- helpers ----------
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) =>
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const fmtRange = (a, b) => (a && b ? `${fmtDate(a)} ~ ${fmtDate(b)}` : '');

  // ---------- states ----------
  const [packageName, setPackageName] = useState('');
  const [spotId, setSpotId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);

  const [displayOrder, setDisplayOrder] = useState('');
  const [priceAdult, setPriceAdult] = useState('');
  const [priceChild, setPriceChild] = useState('');
  const [publishedFlag, setPublishedFlag] = useState(null);

  const [refundFee, setRefundFee] = useState('');
  const [cancelFee, setCancelFee] = useState('');
  const [userCancelable, setUserCancelable] = useState(null);

  const [howToUse, setHowToUse] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [caution, setCaution] = useState('');

  const [ticketArea, setTicketArea] = useState('');
  const [genres, setGenres] = useState([]);

  const [salesRange, setSalesRange] = useState('');
  const [publishRange, setPublishRange] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const salesPickerRef = useRef(null);
  const publishPickerRef = useRef(null);

  // ---- vouchers UI state ----
  const [vouchers, setVouchers] = useState([]);
  const [loadingVouchers, setLoadingVouchers] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState('');
  // table list
  const [boundVouchers, setBoundVouchers] = useState([]); // [{voucherId, voucherName, bindingNumber, displayOrder?}]

  const handleAddVoucher = () => {
    if (!selectedVoucherId) return;
    if (boundVouchers.some(b => b.voucherId === selectedVoucherId)) return; // no dup
    const v = vouchers.find(x => x.voucherId === selectedVoucherId);
    if (!v) return;
    setBoundVouchers(prev => [
      ...prev,
      { voucherId: v.voucherId, voucherName: v.voucherName, bindingNumber: 1 }
    ]);
    setSelectedVoucherId('');
  };

  const handleChangeBinding = (voucherId, value) => {
    const n = Math.max(1, parseInt(value || '1', 10) || 1);
    setBoundVouchers(prev => prev.map(row => row.voucherId === voucherId ? { ...row, bindingNumber: n } : row));
  };

  const handleDeleteVoucher = (voucherId) => {
    setBoundVouchers(prev => prev.filter(row => row.voucherId !== voucherId));
  };

  // --- flatpickr init ---
  useEffect(() => {
    if (!window.flatpickr) return;
    const inputs = Array.from(document.querySelectorAll('#public_range'));
    const ja = window.flatpickr.l10ns?.ja || {};

    if (inputs[0]) {
      salesPickerRef.current = window.flatpickr(inputs[0], {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' }
      });
    }
    if (inputs[1]) {
      publishPickerRef.current = window.flatpickr(inputs[1], {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' }
      });
    }
    return () => {
      try { salesPickerRef.current?.destroy(); } catch { }
      try { publishPickerRef.current?.destroy(); } catch { }
    };
  }, []);

  // --- reflect state -> pickers ---
  useEffect(() => {
    if (salesPickerRef.current && /~|〜|から|-/.test(salesRange)) {
      const [a, b] = salesRange.split(/~|〜|から|-/).map(s => s.trim());
      if (a && b) salesPickerRef.current.setDate([a, b], true);
    }
  }, [salesRange]);

  useEffect(() => {
    if (publishPickerRef.current && /~|〜|から|-/.test(publishRange)) {
      const [a, b] = publishRange.split(/~|〜|から|-/).map(s => s.trim());
      if (a && b) publishPickerRef.current.setDate([a, b], true);
    }
  }, [publishRange]);

  // spots
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingSpots(true);
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(s => ({
          spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
          spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
        })).filter(x => x.spotId && x.spotName);
        setSpots(mapped);
      } catch { setSpots([]); }
      finally { setLoadingSpots(false); }
    })();
    return () => ac.abort();
  }, []);

  // vouchers (master list)
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingVouchers(true);
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/voucher?page=1&size=200`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(v => ({
          voucherId: v.voucherId ?? v.id ?? v.voucher_id ?? '',
          voucherName: v.voucherName ?? v.name ?? v.voucher_name ?? ''
        })).filter(x => x.voucherId && x.voucherName);
        setVouchers(mapped);
      } catch { setVouchers([]); }
      finally { setLoadingVouchers(false); }
    })();
    return () => ac.abort();
  }, []);

  // package detail → prefill, including vouchers
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/package/${packageID}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const p = data?.package || data || {};

        setPackageName(p.packageName || p.package_name || '');
        setSpotId(p.spotId ?? p.spot_id ?? '');
        setDisplayOrder(p.displayOrder ?? p.display_order ?? '');
        setPriceAdult(p.pricePackageAdult ?? p.price_package_adult ?? '');
        setPriceChild(p.pricePackageChild ?? p.price_package_child ?? '');

        if (typeof p.publishedFlag === 'boolean') setPublishedFlag(p.publishedFlag);
        else if (p.published_flag != null) setPublishedFlag(!!p.published_flag);

        setRefundFee(p.refundFeePerUser ?? p.refund_fee_per_user ?? '');
        setCancelFee(p.cancellationFeePerUser ?? p.cancellation_fee_per_user ?? '');

        if (typeof p.userCancelable === 'boolean') setUserCancelable(p.userCancelable);
        else if (p.user_cancelable != null) setUserCancelable(!!p.user_cancelable);

        setHowToUse(p.howToUse ?? p.how_to_use ?? '');
        setTermsOfUse(p.termsOfUse ?? p.terms_of_use ?? '');
        setCaution(p.caution ?? p.caution_text ?? p.caution_note ?? p.caution ?? '');
        setTicketArea(p.ticketArea ?? p.ticket_area ?? '');

        const gRaw = p.ticketGenre ?? p.ticket_genre;
        const gList = typeof gRaw === 'string'
          ? gRaw.split(',').map(s => s.trim()).filter(Boolean)
          : Array.isArray(gRaw) ? gRaw : [];
        setGenres(gList);

        const sSale = p.salesStartDate ?? p.sales_start_date;
        const eSale = p.salesEndDate ?? p.sales_end_date;
        const sPub = p.publishedStartDate ?? p.published_start_date;
        const ePub = p.publishedEndDate ?? p.published_end_date;

        const saleA = sSale ? new Date(sSale) : null;
        const saleB = eSale ? new Date(eSale) : null;
        const pubA = sPub ? new Date(sPub) : null;
        const pubB = ePub ? new Date(ePub) : null;

        setSalesRange(fmtRange(saleA, saleB));
        setPublishRange(fmtRange(pubA, pubB));

        // --- Prefill bound vouchers ---
        // Prefer embedded "vouchers" if present
        let embedded = p.vouchers;
        if (Array.isArray(embedded) && embedded.length) {
          const mapped = embedded.map((r, idx) => ({
            voucherId: r.voucherId ?? r.voucher_id,
            voucherName: r.voucherName ?? r.voucher_name ?? '',
            bindingNumber: Number(r.bindingNumber ?? r.binding_number ?? 1) || 1,
            displayOrder: Number(r.displayOrder ?? r.display_order ?? (idx + 1)) || (idx + 1),
          })).filter(x => x.voucherId);
          setBoundVouchers(mapped);
        } else {
          // fallback: GET /v1/package/:id/vouchers
          try {
            const vr = await fetch(`${API_BASE_URL}/v1/package/${packageID}/vouchers`, {
              headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
              signal: ac.signal
            });
            if (vr.ok) {
              const vdata = await vr.json().catch(() => ({}));
              const items = vdata?.items ?? vdata ?? [];
              const mapped = (Array.isArray(items) ? items : []).map((r, idx) => ({
                voucherId: r.voucherId ?? r.voucher_id,
                voucherName: r.voucherName ?? r.voucher_name ?? '',
                bindingNumber: Number(r.bindingNumber ?? r.binding_number ?? 1) || 1,
                displayOrder: Number(r.displayOrder ?? r.display_order ?? (idx + 1)) || (idx + 1),
              })).filter(x => x.voucherId);
              if (mapped.length) setBoundVouchers(mapped);
            }
          } catch { /* ignore */ }
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError('読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [API_BASE_URL, packageID]);

  // meta
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // range helpers
  const splitRange = (s) => {
    if (!s) return [null, null];
    const sep = /\s*(~|から|-|to)\s*/i;
    const parts = s.split(sep).filter(Boolean);
    const get = (arr) => (arr.length === 3 ? [arr[0], arr[2]] : [arr[0], arr[1]]);
    const [a, b] = get(parts);
    const da = a ? new Date(a) : null;
    const db = b ? new Date(b) : null;
    return [da, db];
  };
  const toISOorNull = (d) => (d && !isNaN(d) ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString() : null);

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || deleting) return;

    const errs = [];
    const fd = new FormData(e.target);

    const pkgName = (fd.get('package_name') || '').trim();
    if (!pkgName) errs.push('パッケージ名を入力してください。');
    if (!spotId) errs.push('スポット名を選択してください。');

    const nAdult = Number(fd.get('ticket_price_adult'));
    if (!Number.isFinite(nAdult) || nAdult < 0) errs.push('大人の金額は0以上の数値で入力してください。');

    const nChild = Number(fd.get('ticket_price_child'));
    if (!Number.isFinite(nChild) || nChild < 0) errs.push('小人の金額は0以上の数値で入力してください。');

    const nRefund = Number(fd.get('ticket_refused_fee'));
    if (!Number.isFinite(nRefund) || nRefund < 0) errs.push('払い戻し手数料は0以上の数値で入力してください。');

    const nCancel = Number(fd.get('ticket_canceled_fee'));
    if (!Number.isFinite(nCancel) || nCancel < 0) errs.push('取り消し手数料は0以上の数値で入力してください。');

    if (publishedFlag === null) errs.push('公開設定（非公開/公開）を選択してください。');
    if (userCancelable === null) errs.push('ユーザーキャンセルフラグを選択してください。');

    // ranges from DOM
    const rangeInputs = Array.from(document.querySelectorAll('#public_range'));
    const salesVal = rangeInputs[0]?.value || '';
    const publishVal = rangeInputs[1]?.value || '';
    const [saleA, saleB] = splitRange(salesVal);
    const [pubA, pubB] = splitRange(publishVal);
    if (!saleA || !saleB || isNaN(saleA) || isNaN(saleB) || saleA > saleB) {
      errs.push('販売期間を「YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm」で正しく入力してください。');
    }
    if (!pubA || !pubB || isNaN(pubA) || isNaN(pubB) || pubA > pubB) {
      errs.push('公開期間を「YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm」で正しく入力してください。');
    }

    if (errs.length) { alert(errs.join('\n')); return; }

    // build payload (now includes vouchers)
    const payload = {
      packageName: pkgName,
      spotId,
      displayOrder: displayOrder === '' ? null : Number(displayOrder),
      pricePackageAdult: nAdult,
      pricePackageChild: nChild,
      publishedFlag: !!publishedFlag,
      salesStartDate: toISOorNull(saleA),
      salesEndDate: toISOorNull(saleB),
      publishedStartDate: toISOorNull(pubA),
      publishedEndDate: toISOorNull(pubB),
      refundFeePerUser: nRefund,
      cancellationFeePerUser: nCancel,
      userCancelable: !!userCancelable,
      ticketArea: ticketArea || null,
      ticketGenre: genres.join(','),

      // ★ send voucher bindings together with package update
      vouchers: boundVouchers.map((r, idx) => ({
        voucherId: r.voucherId,
        bindingNumber: Number(r.bindingNumber) || 1,
        displayOrder: Number(r.displayOrder ?? (idx + 1)) || (idx + 1),
      })),
    };

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/v1/package/${packageID}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `更新に失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      alert('更新しました。');
      navigate('/package');
    } catch (err) {
      alert(err.message || '更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  // delete package
  const handleDelete = async () => {
    if (saving || deleting) return;
    if (!window.confirm('このパッケージを削除しますか？この操作は取り消せません。')) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/v1/package/${packageID}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
        },
      });
      if (!res.ok && res.status !== 204) {
        let msg = `削除に失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      alert('削除しました。');
      navigate('/package');
    } catch (err) {
      alert(err.message || '削除に失敗しました。');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="#!">デジチケ管理</a></li>
            <li className="breadcrumb-item active">編集</li>
          </ol>
        </nav>

        <form className="mb-9" onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">パッケージ編集</h2>
              <h5 className="text-body-tertiary fw-semibold" />
            </div>
            <div className="col-auto">
              <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button"
                onClick={() => window.history.back()} disabled={saving || deleting}>キャンセル</button>
              <button className="btn btn-primary me-2 mb-2 mb-sm-0" type="submit" disabled={saving || deleting}>
                {saving ? '保存中...' : '更新'}
              </button>
              <button className="btn btn-danger mb-2 mb-sm-0" type="button"
                onClick={handleDelete} disabled={saving || deleting}>
                {deleting ? '削除中...' : '削除'}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">パッケージID</h4>
                    <input className="form-control" type="text" name="package_id" disabled defaultValue={packageID} />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">パッケージ名<span className="text-danger">*</span></h4>
                <input className="form-control" type="text" name="package_name" defaultValue={packageName} />
              </div>

              <div className="mb-4">
                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                <div className="input-group">
                  <select className="form-control" value={spotId} onChange={(e) => setSpotId(e.target.value)}
                    disabled={loadingSpots} required>
                    <option value="">{loadingSpots ? '読み込み中...' : '選択してください'}</option>
                    {spots.map(s => (
                      <option key={s.spotId} value={s.spotId}>{s.spotName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">券種</h4>
                    <input className="form-control" type="text" name="" />
                  </div>
                </div>
              </div>

              {/* Vouchers */}
              <div className="mb-4">
                <h4 className="mb-3">バウチャー</h4>
                <div className="input-group">
                  <select className="form-control" value={selectedVoucherId}
                    onChange={(e) => setSelectedVoucherId(e.target.value)}>
                    <option value="">{loadingVouchers ? '読み込み中...' : '選択してください'}</option>
                    {vouchers.map(v => (
                      <option key={v.voucherId} value={v.voucherId}>{v.voucherName}</option>
                    ))}
                  </select>
                  <button className="btn btn-outline-secondary" type="button"
                    onClick={handleAddVoucher} disabled={!selectedVoucherId}>＋</button>
                </div>

                <table className="table fs-9 mb-0 mt-3">
                  <thead>
                    <tr>
                      <th className="align-middle ps-3" style={{ width: 40 }}></th>
                      <th className="align-middle ps-3">バウチャーID</th>
                      <th className="align-middle ps-3">バウチャー名</th>
                      <th className="align-middle ps-3" style={{ width: 140 }}>綴り枚数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boundVouchers.length === 0 ? (
                      <tr><td className="ps-3" colSpan={4}>未追加です</td></tr>
                    ) : (
                      boundVouchers.map(row => (
                        <tr key={row.voucherId}>
                          <td className="ps-3">
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              title="削除"
                              aria-label="削除"
                              onClick={() => handleDeleteVoucher(row.voucherId)}
                              style={{ fontSize: 18, lineHeight: 1, textDecoration: 'none' }}
                            >
                              ×
                            </button>
                          </td>
                          <td className="ps-3">{row.voucherId}</td>
                          <td className="ps-3">{row.voucherName}</td>
                          <td className="ps-3">
                            <input
                              type="number"
                              min="1"
                              className="form-control form-control-sm"
                              value={row.bindingNumber}
                              onChange={(e) => handleChangeBinding(row.voucherId, e.target.value)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Prices */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">大人<span className="text-danger">*</span></h4>
                    <div className="d-flex align-items-center gap-3">
                      <div className="input-group">
                        <input className="form-control" type="number" name="ticket_price_adult" min="0" defaultValue={priceAdult ?? ''} />
                        <span className="input-group-text">円</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4 col-md-6">
                  <h4 className="mb-3">小人<span className="text-danger">*</span></h4>
                  <div className="d-flex align-items-center gap-3">
                    <div className="input-group">
                      <input className="form-control" type="number" name="ticket_price_child" min="0" defaultValue={priceChild ?? ''} />
                      <span className="input-group-text">円</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fees */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">払い戻し手数料<span className="text-danger">*</span></h4>
                    <div className="d-flex align-items-center gap-3">
                      <div className="input-group">
                        <input className="form-control" type="number" name="ticket_refused_fee" min="0" defaultValue={refundFee ?? ''} />
                        <span className="input-group-text">円</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4 col-md-6">
                  <h4 className="mb-3">取り消し手数料<span className="text-danger">*</span></h4>
                  <div className="d-flex align-items-center gap-3">
                    <div className="input-group">
                      <input className="form-control" type="number" name="ticket_canceled_fee" min="0" defaultValue={cancelFee ?? ''} />
                      <span className="input-group-text">円</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* User cancel */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">ユーザーキャンセルフラグ</h4>
                    <div className="d-flex align-items-center gap-3">
                      <label className="mb-0">
                        <input type="radio" name="ticket_user_cancel_flag" value="0"
                          checked={userCancelable === false} onChange={() => setUserCancelable(false)} /> 否
                      </label>
                      <label className="mb-0">
                        <input type="radio" name="ticket_user_cancel_flag" value="1"
                          checked={userCancelable === true} onChange={() => setUserCancelable(true)} /> 可
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mb-4 col-md-6" />
              </div>

              {/* Texts */}
              <div className="mb-4">
                <h4 className="mb-3">使用方法</h4>
                <textarea className="form-control" name="content" rows={4} defaultValue={howToUse}></textarea>
              </div>
              <div className="mb-4">
                <h4 className="mb-3">利用条件</h4>
                <textarea className="form-control" name="content" rows={4} defaultValue={termsOfUse}></textarea>
              </div>
              <div className="mb-4">
                <h4 className="mb-3">注意事項</h4>
                <textarea className="form-control" name="content" rows={4} defaultValue={caution}></textarea>
              </div>

              {/* Area */}
              <div className="mb-4">
                <h4 className="mb-3">エリア</h4>
                <select className="form-control" name="area" value={ticketArea} onChange={(e) => setTicketArea(e.target.value)}>
                  <option value="">選択してください</option>
                  {AREA_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right column */}
            <div className="col-12 col-xl-4">
              <div className="row g-2">
                <div className="col-12 col-xl-12">
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="row gx-3">
                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className="mb-4">
                            <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                            <div className="d-flex align-items-center gap-3">
                              <label className="mb-0">
                                <input type="radio" name="publish_status" value="0"
                                  checked={publishedFlag === false} onChange={() => setPublishedFlag(false)} /> 非公開
                              </label>
                              <label className="mb-0">
                                <input type="radio" name="publish_status" value="1"
                                  checked={publishedFlag === true} onChange={() => setPublishedFlag(true)} /> 公開
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Sales */}
                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className="mb-4">
                            <div className="d-flex flex-wrap mb-2">
                              <h5 className="mb-0 text-body-highlight me-2">販売期間<span className="text-danger">*</span></h5>
                            </div>
                            <input className="form-control" type="text" name="public_range" id="public_range"
                              placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm" defaultValue={salesRange} />
                          </div>
                        </div>

                        {/* Publish */}
                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className="mb-4">
                            <div className="d-flex flex-wrap mb-2">
                              <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                            </div>
                            <input className="form-control" type="text" name="public_range" id="public_range"
                              placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm" defaultValue={publishRange} />
                          </div>
                        </div>

                        {/* Order */}
                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className="mb-4">
                            <div className="d-flex flex-wrap mb-2">
                              <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                            </div>
                            <input className="form-control" type="number" name="order" defaultValue={displayOrder ?? ''} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Genres */}
                <div className="col-12 col-xl-12">
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="row gx-3">
                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className="mb-4">
                            <div className="d-flex flex-wrap mb-2">
                              <h5 className="mb-0 text-body-highlight me-2">ジャンル<span className="text-danger">*</span></h5>
                            </div>
                            {TICKET_GENRE_OPTIONS.map(([value, label]) => (
                              <label key={value} className="me-3">
                                <input
                                  type="checkbox"
                                  name="genre[]"
                                  value={value}
                                  checked={genres.includes(value)}
                                  onChange={(e) => {
                                    if (e.target.checked) setGenres([...genres, value]);
                                    else setGenres(genres.filter(g => g !== value));
                                  }}
                                /> {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image block (unchanged) */}
                <div className="col-12 col-xl-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-4">パッケージ画像</h4>
                      <div className="dropzone dropzone-multiple p-0 mb-5" id="my-awesome-dropzone" data-dropzone="data-dropzone">
                        <div className="fallback"><input name="file" type="file" multiple /></div>
                        <div className="dz-preview d-flex flex-wrap"></div>
                        <div className="dz-message text-body-tertiary text-opacity-85" data-dz-message="data-dz-message">
                          Drag your photo here<span className="text-body-secondary px-1">or</span>
                          <button className="btn btn-link p-0" type="button">Browse from device</button><br />
                          <img className="mt-3 me-2" src="../../../assets/img/icons/image-icon.png" width="40" alt="" />
                        </div>
                      </div>
                      {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
                      {!loading && error && <div className="text-danger mt-3">{error}</div>}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </form>
      </>
    </Layout>
  );
}
